import {
  useState,
  useEffect,
  ReactNode,
  useCallback,
  ReactElement,
  ChangeEvent,
  MouseEvent,
  useMemo,
  useId,
} from 'react';

import {
  addDays,
  formatISO,
  format as formatDate,
  parse as parseDate,
  parseISO,
  subDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { ruRU } from '@mui/x-date-pickers/locales';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DateCalendar as MUICalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Popover from '@mui/material/Popover';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import classNames from 'classnames';

import { CALENDAR_MIN_DATE, CALENDAR_MAX_DATE } from '../../services/calendar';
import { useCalendar } from '../../services/hooks';
import { Typography } from '../../Typography';
import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { toDate } from '../../format';
import InputMask from '../../ReactInputMask';

import { CalendarHeader } from './CalendarHeader';
import { Day, DayProps } from './Day';
import { useStyles } from './DateInput.styles';

const DATE_FORMAT = 'dd.MM.yyyy';
const MASK_PLACEHOLDER = '_';
const EMPTY_INPUT_MASK = '__.__.____';

/**
 * Свойства компонента {@link DateInput}
 */
export interface DateInputProps {
  /**
   * Метка для поля ввода.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Имя поля для использования в формах.
   */
  name?: string;

  /**
   * Функция, вызываемая при изменении выбранной даты.
   */
  onChange: (date: string | undefined) => void;

  /**
   * Функция, вызываемая при очистке поля.
   */
  onClear?: () => void;

  /**
   * Функция, вызываемая при потере фокуса.
   */
  onBlur?: () => void;

  /**
   * Функция для логирования действия.
   */
  onLogAction?: (type: string) => void;

  /**
   * Текст плейсхолдера для поля ввода.
   *
   * @defaultValue `'ДД.ММ.ГГГГ'`
   */
  placeholder?: string;

  /**
   * Функция для отключения определённых дат.
   */
  shouldDisableDate?: (date: Date | null) => boolean;

  /**
   * Является ли поле обязательным.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Отключены ли прошедшие даты.
   *
   * @defaultValue `false`
   */
  disablePast?: boolean;

  /**
   * Отключено ли поле ввода.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Минимальная дата, доступная для выбора.
   *
   * @defaultValue `CALENDAR_MIN_DATE`
   */
  minDate?: Date;

  /**
   * Максимальная дата, доступная для выбора.
   *
   * @defaultValue `CALENDAR_MAX_DATE`
   */
  maxDate?: Date;

  /**
   * Распознанное значение (автозаполнение).
   */
  recognizedValue?: string;

  /**
   * Было ли использовано распознавание ранее.
   */
  isRecognitionUsedBefore?: boolean;

  /**
   * Внешний текст ошибки.
   */
  externalError?: string;

  /**
   * Другая выбранная дата в диапазоне.
   */
  otherSelectedDate?: string;

  /**
   * Текущее выбранное значение даты.
   */
  value?: string;

  /**
   * Может ли поле ввода быть очищено.
   *
   * @defaultValue `false`
   */
  clearable?: boolean;

  /**
   * Всегда ли отображаться кнопка очистки.
   *
   * @defaultValue `false`
   */
  alwaysShowClear?: boolean;

  /**
   * Текст ошибки, если поле ввода содержит ошибку.
   */
  error?: string;

  /**
   * Текст предупреждения.
   */
  warning?: string;
  warnings?: string[];

  /**
   * Отображается ли диапазон дат.
   *
   * @defaultValue `false`
   */
  showRange?: boolean;

  /**
   * Загружаются ли данные.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Текст подсказки.
   */
  tooltip?: ReactNode | null;

  /**
   * Уникальный идентификатор для поля ввода.
   */
  id?: string;

  /**
   * Опциональный ID для тестирования компонента.
   */
  testId?: string;

  /**
   * Функция, вызываемая при потере фокуса полем ввода.
   */
  formBlur?: (name: string) => void;

  /**
   * Функция, которая рендерит тултип для дня в календаре.
   */
  renderDayTooltip?: (day: Date | null) => ReactElement | null;

  /**
   * Функция, которая рендерит что-либо под цифрой даты дня в календаре.
   */
  renderDayFooter?: (
    day: Date | null,
    isInRange: boolean,
  ) => ReactElement | null;
  customCalendarDownload?: (date: Date) => Promise<void>;
}

/**
 * Компонент для выбора даты с выпадающим календарем.
 *
 * @public
 * @param label - Метка для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param name - Имя поля для использования в формах.
 * @param onChange - Функция, вызываемая при изменении выбранной даты.
 * @param formBlur - Функция, вызываемая при потере фокуса полем ввода.
 * @param onClear - Функция, вызываемая при очистке поля.
 * @param onLogAction - Функция для логирования действия.
 * @param placeholder - Текст плейсхолдера для поля ввода.
 * @param required - Является ли поле обязательным.
 * @param disablePast - Отключены ли прошедшие даты.
 * @param disabled - Отключено ли поле ввода.
 * @param minDate - Минимальная дата, доступная для выбора.
 * @param maxDate - Максимальная дата, доступная для выбора.
 * @param recognizedValue - Распознанное значение (автозаполнение).
 * @param isRecognitionUsedBefore - Было ли использовано распознавание ранее.
 * @param shouldDisableDate - Функция для отключения определённых дат.
 * @param externalError - Внешний текст ошибки.
 * @param otherSelectedDate - Другая выбранная дата в диапазоне.
 * @param clearable - Может ли поле ввода быть очищено.
 * @param alwaysShowClear - Всегда ли отображаться кнопка очистки.
 * @param error - Текст ошибки, если поле ввода содержит ошибку.
 * @param warning - Текст предупреждения.
 * @param value - Текущее выбранное значение даты.
 * @param onBlur - Функция, вызываемая при потере фокуса.
 * @param loading - Загружаются ли данные.
 * @param showRange - Отображается ли диапазон дат.
 * @param tooltip - Текст подсказки.
 * @param testId - Опциональный ID для тестирования компонента.
 * @param id - Уникальный идентификатор для поля ввода.
 * @param renderDayTooltip - Функция, которая рендерит тултип для дня в календаре.
 * @param renderDayFooter - Функция, которая рендерит что-либо под цифрой даты дня в календаре.
 * @param customCalendarDownload - Функция, вызываемая  вместо calendar.download().
 * @param warnings - Предупреждения, отображаемые под полем при отсутствии ошибок.
 * @returns Возвращает JSX-элемент представляющий компонент DateInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DateInputProps}
 */
export const DateInput = ({
  label,
  labelIcon,
  name,
  onChange,
  formBlur,
  onClear,
  onLogAction,
  placeholder = 'ДД.ММ.ГГГГ',
  required = false,
  disablePast = false,
  disabled = false,
  minDate = CALENDAR_MIN_DATE,
  maxDate = CALENDAR_MAX_DATE,
  recognizedValue,
  isRecognitionUsedBefore,
  shouldDisableDate,
  externalError,
  otherSelectedDate,
  clearable = false,
  alwaysShowClear = false,
  error,
  warning,
  warnings,
  value,
  onBlur,
  loading = false,
  showRange = false,
  tooltip = null,
  testId = 'test-id-date-input-text-field',
  id,
  renderDayTooltip,
  renderDayFooter,
  customCalendarDownload,
  ...rest
  // eslint-disable-next-line complexity
}: DateInputProps) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);
  const calendar = useCalendar();

  const valueAsDate = useMemo(() => (value ? parseISO(value) : null), [value]);

  const [inputValue, setInputValue] = useState<string>(
    value ? formatDate(new Date(value), DATE_FORMAT, { locale: ru }) : '',
  );

  const [errorByShouldDisableDate, setErrorByShouldDisableDate] = useState<
    boolean | 'force'
  >(false);
  const [errorText, setErrorText] = useState(error);
  const [loaded, setLoaded] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const open = Boolean(anchorEl);

  const getMaxDateText = useCallback(() => {
    if (!maxDate) return '';

    if (maxDate instanceof Date) return addDays(maxDate, 1);

    return maxDate;
  }, [maxDate]);

  /**
   * Функция, возвращающая текст ошибки при валидации даты.
   */
  const getErrorText = useCallback(
    (date: Date | null) => {
      if (!date) return undefined; // Обязательность поля не мы решаем

      if (!calendar.isValidDate(date)) {
        return 'Некорректная дата';
      }

      if (calendar.startOfDay(new Date(minDate)) > calendar.startOfDay(date)) {
        return `Дата должна быть больше чем ${toDate(
          subDays(new Date(minDate), 1).toString(),
        )}`;
      }

      if (calendar.startOfDay(new Date(maxDate)) < calendar.startOfDay(date)) {
        return `Дата должна быть меньше чем ${toDate(
          getMaxDateText().toString(),
        )}`;
      }

      setErrorByShouldDisableDate(!!shouldDisableDate?.(date));

      return undefined;
    },
    [calendar, shouldDisableDate, getMaxDateText, maxDate, minDate],
  );

  /**
   * Функция обработки изменения даты в компоненте DateInput.
   */
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLoaded(false);

      const newValue = event.target.value;
      setInputValue(newValue);

      if (!newValue || newValue === EMPTY_INPUT_MASK) {
        onChange(undefined);
        formBlur?.(name as string);
        setLoaded(true);
        return;
      }

      if (newValue.includes(MASK_PLACEHOLDER)) {
        // Дата введена не полностью — не сбрасываем значение и не показываем ошибку
        formBlur?.(name as string);
        setLoaded(true);
        return;
      }

      const newValueAsDate = parseDate(newValue, DATE_FORMAT, new Date());

      if (newValueAsDate instanceof Date && !isNaN(newValueAsDate.valueOf())) {
        if (customCalendarDownload) {
          customCalendarDownload(newValueAsDate);
        } else {
          calendar.download(newValueAsDate);
        }
      }

      const errorMessage = getErrorText(newValueAsDate);
      if (errorMessage) {
        if (value) onChange(undefined);

        setErrorText(errorMessage);
      } else {
        const newValueIso = formatISO(newValueAsDate, {
          representation: 'date',
        });
        onLogAction?.('Введена дата');
        onChange(newValueIso);
        setErrorText(error);
      }

      formBlur?.(name as string);
      setLoaded(true);
    },
    [
      calendar,
      customCalendarDownload,
      value,
      getErrorText,
      onChange,
      onLogAction,
      name,
      formBlur,
      error,
    ],
  );

  /**
   * Обработчик потери фокуса текстовым полем.
   */
  const handleInputBlur = useCallback(() => {
    if (
      calendar.isValidDate(valueAsDate) &&
      !errorText &&
      !inputValue.includes(MASK_PLACEHOLDER)
    ) {
      const newValueIso = valueAsDate
        ? formatISO(new Date(valueAsDate), {
            representation: 'date',
          })
        : undefined;
      onChange(newValueIso);

      formBlur?.(name as string);
      return;
    }

    if (value && !error) {
      const oldValueAsDate = new Date(value);
      setInputValue(formatDate(oldValueAsDate, 'dd.MM.yyyy', { locale: ru }));
    } else {
      setInputValue('');
      if (value) onChange(undefined);
    }

    formBlur?.(name as string);
    setErrorText(
      inputValue !== EMPTY_INPUT_MASK ? (errorText ?? error) : error,
    );
  }, [
    calendar,
    name,
    formBlur,
    valueAsDate,
    errorText,
    value,
    error,
    inputValue,
    onChange,
  ]);

  useEffect(() => {
    if (value) {
      const asDate = new Date(value);
      setInputValue(formatDate(asDate, DATE_FORMAT, { locale: ru }));
    } else {
      setInputValue(''); // Фикс бага 53638, коммент добавил на случай если где-то что-то сломается
    }
  }, [value]);

  useEffect(() => {
    if (error && errorByShouldDisableDate !== 'force') {
      setErrorText(error);

      return;
    } else if (errorByShouldDisableDate) {
      setErrorByShouldDisableDate('force');
      setErrorText('Некорректная дата');
      onChange(undefined);
    } else {
      setErrorText(undefined);
    }
  }, [error, errorByShouldDisableDate, onChange]);

  useEffect(() => {
    if (recognizedValue && !value) {
      onChange(recognizedValue);

      formBlur?.(name as string);
    }
  }, [onChange, name, formBlur, recognizedValue, value]);

  useEffect(() => {
    if (onBlur && isRecognitionUsedBefore && !disabled && !recognizedValue) {
      onBlur();
    }
  }, [isRecognitionUsedBefore, disabled, onBlur]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customCalendarDownload) {
      customCalendarDownload(new Date()).then(() => setLoaded(true));
    } else {
      calendar.download(new Date()).then(() => setLoaded(true));
    }
  }, [calendar, customCalendarDownload]);

  /**
   * Функция для очистки значения даты.
   */
  const clearDateInput = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setInputValue('');
      setLoaded(false);
      setShowClear(false);
      setAnchorEl(null);
      setErrorText(error);
      setErrorByShouldDisableDate(false);
      onChange(undefined);
      formBlur?.(name as string);
      onBlur?.();
      onClear?.();
      setTimeout(() => setLoaded(true), 500);
    },
    [error, onChange, formBlur, name, onBlur, onClear],
  );

  /**
   * Обработчик клика по иконке календаря.
   */
  const dateIconClickHandler = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (loaded && !disabled) {
        onLogAction?.('Открыть календарь');
        setAnchorEl(e.currentTarget);
      }
    },
    [disabled, loaded, onLogAction],
  );

  /**
   * Флаг, указывающий на то, что данные загружаются.
   */
  const isLoading = !loaded || loading;

  /**
   * Функция для определения цвета иконки календаря.
   *
   * @param isDisabled - Флаг, указывающий, что компонент отключен.
   * @param isError - Флаг, указывающий, что есть ошибка.
   * @returns Цвет иконки: 'disabled', 'error' или 'primary'.
   */
  const getCalendarIconColor = useCallback(
    (isDisabled?: boolean, isError?: boolean) => {
      if (isDisabled) {
        return 'stroke.secondary';
      }
      if (isError) {
        return 'accent.text.error';
      }

      return 'original.brand.primary';
    },
    [],
  );

  /**
   * Функция для рендеринга окончания текстового поля (адорнмента).
   *
   * @param props - Свойства текстового поля.
   * @returns  - JSX элемент для окончания текстового поля.
   */
  const renderEndAdornment = useCallback(
    (props: { value: string; disabled?: boolean; error?: boolean }) => {
      if (isLoading) {
        return <Preloader />;
      }

      const propsValueAsDate = parseDate(props.value, DATE_FORMAT, new Date());

      return (
        <Box display="flex">
          {clearable &&
            calendar.isValidDate(propsValueAsDate) &&
            !props.disabled && (
              <Box
                display="flex"
                alignItems="center"
                className={classes.clearIcon}
                mr="12"
                height={24}
                testId={`${testId}-clear-button`}
                onClick={clearDateInput}
              >
                <Icon
                  name="dismiss_substract"
                  size="24"
                  color={props.error ? 'accent.text.error' : 'stroke.secondary'}
                />
              </Box>
            )}
          <Box
            testId={`${testId}-calendar-button`}
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              if (loaded && !props.disabled) {
                setAnchorEl(e.currentTarget);
              }
            }}
          >
            <Box
              onClick={dateIconClickHandler}
              display="flex"
              alignItems="center"
            >
              <Icon
                name="calendar_outline"
                size="24"
                color={getCalendarIconColor(props.disabled, !!props.error)}
              />
            </Box>
          </Box>
        </Box>
      );
    },
    [
      calendar,
      classes.clearIcon,
      clearDateInput,
      clearable,
      dateIconClickHandler,
      getCalendarIconColor,
      isLoading,
      loaded,
      testId,
    ],
  );

  return (
    <FormControl className={classNames(classes.input, 'aqa_date_input')}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ru}
        localeText={
          ruRU.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        {label && (
          <Label
            label={label}
            required={required}
            tooltip={tooltip}
            icon={labelIcon}
            inputId={inputId}
          />
        )}
        <Box>
          <InputMask
            mask={isLoading ? '' : '99.99.9999'}
            maskPlaceholder={MASK_PLACEHOLDER}
            value={isLoading ? 'Загрузка...' : inputValue}
            data-testid={testId}
            alwaysShowMask={false}
            disabled={isLoading || disabled}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
          >
            <TextField
              id={inputId}
              classes={{ root: classes.textDateInput }}
              variant="outlined"
              name={name}
              fullWidth
              placeholder={placeholder || label}
              disabled={isLoading || disabled}
              {...(errorText && {
                error: !!externalError || !!errorText,
                helperText: externalError || errorText,
              })}
              {...(isLoading && {
                value: 'Загрузка...',
              })}
              slotProps={{
                input: {
                  placeholder: placeholder,
                  className: 'textFieldInput aqa_dt_input',
                  classes: { adornedEnd: classes.adornedEnd },
                  endAdornment: renderEndAdornment({
                    value: inputValue,
                    disabled,
                    error: !!errorText,
                  }),
                },
              }}
            />
          </InputMask>
          {!externalError && !errorText && warning && (
            <Typography variant="caption" color="text.light.secondary">
              {warning}
            </Typography>
          )}
          {!externalError && !errorText && warnings && warnings.length > 0 && (
            <Box display="flex" flexDirection="column" gap="4">
              {warnings.map((warningText, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  color="text.light.secondary"
                >
                  {warningText}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            onBlur?.();
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box className="aqa_calendar" pb="16" testId={`${testId}-calendar`}>
            <MUICalendar
              {...rest}
              view="day"
              dayOfWeekFormatter={(day) =>
                formatDate(new Date(day), 'EEEEEE', { locale: ru })
              }
              value={valueAsDate}
              onMonthChange={
                customCalendarDownload
                  ? customCalendarDownload
                  : (date) => calendar.download(date || undefined)
              }
              disablePast={disablePast}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(date, isFinal) => {
                if (isFinal) {
                  onLogAction?.('Выбрана дата');
                  onChange(
                    date
                      ? formatISO(date, { representation: 'date' })
                      : undefined,
                  );
                  setErrorText(error);
                  setErrorByShouldDisableDate(false);
                  setAnchorEl(null);
                  setTimeout(() => onBlur?.());
                  setTimeout(() => setShowClear(true), 200);
                }
              }}
              {...(shouldDisableDate ? { shouldDisableDate } : {})}
              slots={{
                calendarHeader: CalendarHeader,
                day: Day,
              }}
              slotProps={{
                day: {
                  onMouseEnter: (_, day) => {
                    setHoverDate(day);
                  },
                  onMouseLeave: (_, day) => {
                    if (
                      day &&
                      hoverDate &&
                      calendar.isSameDay(day, hoverDate)
                    ) {
                      setHoverDate(null);
                    }
                  },
                  testHoliday: (day) =>
                    !calendar.isSameDay(day, new Date()) &&
                    calendar.isHolidayOrWeekend(day),
                  testIsInRange: (day) => {
                    if (hoverDate || valueAsDate) {
                      const date = (hoverDate || valueAsDate) as Date | string;

                      return !!(
                        showRange &&
                        day &&
                        otherSelectedDate &&
                        calendar.startOfDay(new Date(otherSelectedDate)) <=
                          day &&
                        calendar.startOfDay(new Date(date)) >= day
                      );
                    }

                    return false;
                  },
                  testIsStart: (day) =>
                    !!(
                      otherSelectedDate &&
                      day &&
                      calendar.isSameDay(day, new Date(otherSelectedDate))
                    ),
                  testIsEnd: (day) => {
                    if (
                      (valueAsDate &&
                        calendar.isSameDay(day, new Date(valueAsDate))) ||
                      (otherSelectedDate &&
                        calendar.isSameDay(day, new Date(otherSelectedDate)))
                    ) {
                      return (
                        valueAsDate &&
                        !hoverDate &&
                        calendar.isSameDay(day, new Date(valueAsDate))
                      );
                    }

                    return hoverDate && calendar.isSameDay(day, hoverDate);
                  },
                  renderDayTooltip: renderDayTooltip,
                  renderDayFooter: renderDayFooter,
                } as Partial<DayProps>,
              }}
            />
            {clearable && value && (alwaysShowClear || showClear) && (
              <Box pt="16" px="16" onClick={clearDateInput}>
                <a href="#" className={classes.action}>
                  Сбросить
                </a>
              </Box>
            )}
          </Box>
        </Popover>
      </LocalizationProvider>
    </FormControl>
  );
};
